/* @flow */
import React from 'react';
import { withRouter } from 'react-router';

import CurrentUser from 'auth/CurrentUser';
import Glyph from 'components/Glyph.jsx';
import Group from 'entities/Group';
import GroupAvatar from 'groups/components/GroupAvatar.jsx';
import GroupName from 'groups/components/GroupName.jsx';
import GroupSettingsModal from 'groups/components/GroupSettingsModal.jsx';
import GroupSubscribedSettingModal from 'groups/components/GroupSubscribedSettingModal.jsx';
import SchoolSettingsModal from 'groups/components/SchoolSettingsModal.jsx';
import GroupAspectTabs from 'groups/components/GroupAspectTabs.jsx';
import OrgAspectTabs from 'groups/components/OrgAspectTabs.jsx';
import Link from 'components/Link.jsx';
import ModalTrigger from 'components/ModalTrigger.jsx';
import GroupStore from 'groups/stores/GroupStore';
import OrganizationStore from 'stores/OrganizationStore';
import RootGroup, { rootGroup } from 'entities/RootGroup';

import styles from './styles/ClassNavigationHeader.css';

type Props = {
  params: {groupCode?: string, organizationId?: string},
  // XXX should come through Flux
  router: Object,
};

export class ClassNavigationHeader extends React.Component {
  props: Props;

  state = {};

  componentWillMount() {
    this.setGroupOrOrg(this.props.params);
  }

  componentWillReceiveProps(nextProps: Props) {
    this.setGroupOrOrg(nextProps.params);
  }

  shouldComponentUpdate(nextProps: Props) {
    const nextGroup = this.getGroupOrOrg(nextProps.params);
    const groupHasChanged = nextGroup !== this.state.groupOrOrg;
    const tabHasChanged = nextProps.router.location.pathname !== window.prevLocation;

    return nextGroup && (groupHasChanged || tabHasChanged);
  }

  getGroupOrOrg(params: Object) {
    const { groupCode } = params;
    let organizationId;
    if (params.organizationId) {
      organizationId = parseInt(params.organizationId, 10);
    }

    let result;
    if (organizationId) {
      result = OrganizationStore.getOrganization(organizationId);
    } else if (groupCode) {
      result = GroupStore.getGroupForCode(groupCode);
    }

    return result || rootGroup;
  }

  setGroupOrOrg(params: Object) {
    const groupOrOrg = this.getGroupOrOrg(params);
    this.setState({ groupOrOrg });
  }

  // Upon changing the group code, we must redirect to the new group code
  handleGroupChanged = (groupCode: string) => {
    this.props.router.push(`/classes/${groupCode}`);
  };

  shouldLinkHeader(): boolean {
    const { groupOrOrg } = this.state;
    return (this.isGroup() && groupOrOrg.getType() !== 'root');
  }


  shouldShowCode(): boolean {
    const { groupOrOrg } = this.state;
    return (this.isGroup() && groupOrOrg.getType() !== 'root');
  }

  isGroup(): boolean {
    const { groupOrOrg } = this.state;
    return groupOrOrg instanceof Group || groupOrOrg instanceof RootGroup;
  }

  renderSettingsIcon() {
    if (this.shouldLinkHeader()) {
      return (
        <ModalTrigger modal={this.renderGroupSettingsModal()}>
          <Glyph className={styles.settingsIcon} name="gear" size="large" solo />
        </ModalTrigger>
      );
    }
    return null;
  }

  renderAvatar() {
    const avatar = <GroupAvatar group={this.state.groupOrOrg} className={styles.avatar} />;

    if (this.shouldLinkHeader()) {
      return (
        <ModalTrigger modal={this.renderGroupSettingsModal()}>
          <Link>
            {avatar}
          </Link>
        </ModalTrigger>
      );
    } else {
      return avatar;
    }
  }

  renderName() {
    let groupName = (
      <div className={styles.groupName}>
        <GroupName group={this.state.groupOrOrg} />
      </div>
    );
    if (this.shouldLinkHeader()) {
      groupName = (
        <ModalTrigger modal={this.renderGroupSettingsModal()}>
          {groupName}
        </ModalTrigger>
      );
    }
    return groupName;
  }

  renderCode() {
    if (!this.shouldShowCode()) {
      return null;
    }

    const { groupOrOrg } = this.state;
    const codeText = `@${groupOrOrg.getCode()}`;
    const canJoinText = groupOrOrg.isStaffClass() ?
      'Staff, students, and parents can join your school by ' :
      'Students and parents can join your class by ';

    const smsNumber = groupOrOrg.getSMSNumber();
    const actionText = smsNumber ?
      `texting ${codeText} to ${smsNumber}` :
      `entering ${codeText} in the app`;
    const codeTooltip = canJoinText + actionText;

    const link = (
      <Link
        color="white"
        className={styles.groupCode}
        tooltip={codeTooltip}
        tooltipPlacement="bottom">
        {codeText}
      </Link>
    );

    if (this.shouldLinkHeader()) {
      return (
        <span>
          <ModalTrigger checkForUnsavedChanges modal={this.renderGroupSettingsModal()}>
            {link}
          </ModalTrigger>
        </span>
      );
    } else {
      return link;
    }
  }

  renderGroupSettingsModal() {
    const { groupOrOrg } = this.state;
    if (this.isGroup() && groupOrOrg.isStaffClass()) {
      return <SchoolSettingsModal group={groupOrOrg} />;
    } else if (groupOrOrg.canEdit()) {
      return (
        <GroupSettingsModal
          group={groupOrOrg}
          onCodeChange={this.handleGroupChanged}
          userIsTeacher={CurrentUser.getUser().isTeacher()}
        />
      );
    } else {
      return <GroupSubscribedSettingModal group={groupOrOrg} />;
    }
  }

  renderAspectTabs(isGroup: boolean) {
    const { groupOrOrg } = this.state;

    if (isGroup) {
      return <GroupAspectTabs group={groupOrOrg} />;
    }

    return OrgAspectTabs.shouldMount()
      ? <OrgAspectTabs org={groupOrOrg} />
      : null;
  }

  render() {
    const { groupOrOrg } = this.state;
    const isGroup = this.isGroup();

    const aspectTabs = this.renderAspectTabs(isGroup);

    return (
      <div className={styles.this}>
        {isGroup && this.renderSettingsIcon()}
        <div
          className={styles.top}
          style={{ background: groupOrOrg.getAccentColor() }}>
          {this.renderAvatar()}
          {this.renderName()}
          {isGroup && this.renderCode()}
        </div>
        {aspectTabs &&
          <div className={styles.bottom}>
            {aspectTabs}
          </div>
        }
      </div>
    );
  }
}

export default withRouter(ClassNavigationHeader);



// WEBPACK FOOTER //
// ./src/groups/components/ClassNavigationHeader.jsx